import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { ResearchId } from "../../../adapters/insights-agent/state/AppState.ts";
import {
  useResearchesState,
  useTermsAndConditionsState,
} from "../../../adapters/insights-agent/state/hooks/stateHooks.ts";
import { useInsightsAgentService } from "../../../adapters/insights-agent/state/hooks/useInsightsAgentService.tsx";
import { useFeatureFlagValue } from "../../../config/FeatureFlagManager/hooks.ts";
import { HistorySidebar as HistorySidebarLegacy } from "../../../feature-components/HistorySidebar/HistorySidebar.tsx";
import { HistorySidebarRedesign } from "../../../feature-components/HistorySidebar/HistorySidebarRedesign.tsx";

export const ConnectedHistorySidebar: React.FC = () => {
  const appService = useInsightsAgentService();
  const historyState = useResearchesState();
  const historyItems = useMemo(
    () =>
      Object.values(historyState.items)
        .filter((item) => {
          return (
            item.status === "completed" ||
            item.status === "processing_meta_analysis"
          );
        }) // Filter out deleted items
        .sort((a, b) => {
          return Date.parse(b.update_time) - Date.parse(a.update_time);
        })
        .map((item) => ({
          value: item.id,
          label: item.display_name || item.brief,
          data: {
            brief: item.brief,
            questions: item.questions,
          },
        })),
    [historyState.items],
  );
  const termsState = useTermsAndConditionsState();
  const navigate = useNavigate();
  const { id } = useParams();
  const enableRedesign = useFeatureFlagValue("enableHistorySidebarRedesign");
  const HistorySidebar = enableRedesign
    ? HistorySidebarRedesign
    : HistorySidebarLegacy;
  return (
    <HistorySidebar
      items={historyItems}
      onNewChat={() => {
        navigate("/workflows/new");
      }}
      onSelectionChange={(id) => {
        navigate(`/workflows/${id}`);
      }}
      onDelete={(researchId) => {
        if (researchId === id) navigate("/workflows/new");
        const confirmed = window.confirm(
          "Are you sure you want to delete this workflow?",
        );
        if (!confirmed) return;
        appService.deleteResearch({ researchId }).then(() => {});
      }}
      onRename={(researchId) => {
        const newName = window.prompt("Enter new name for workflow", "");
        if (!newName) return;
        appService.renameResearch({ researchId, name: newName }).then(() => {});
      }}
      loading={historyState.loading}
      activeItem={id as ResearchId}
      enableTermsAndConditions={termsState.status !== "acceptance_not_required"}
    />
  );
};
